/**
 * Aktenraum F/E (Phillip Hengl): Ordner-pro-Fall führen.
 * Suchen / Zitieren / Verweisen / Zusammenfassen / Subsumieren / Verhandeln / Schreiben
 * auf angehakte Docs; „Nicht relevant“ nimmt ein Doc aus der ARKO-Auswahl (kein LLM).
 * Vite-frei — Units via portal_node_ts. Kein PB, kein Voll-PDF.
 */

export const PORTAL_AKTE_FUEHREN_MARKER = 'akte-fuehren:ordner-pro-fall';
export const PORTAL_AKTE_SUBSUMTION_MARKER = 'akte-fuehren:subsumtion';
export const PORTAL_AKTE_VARIANT_EXTRACT_MARKER = 'akte-fuehren:varianten-auszuege';
export const PORTAL_AKTE_SCHREIBEN_MARKER = 'akte-fuehren:schreiben';
/** Sichtbarer Hinweis unter dem Chip Schreiben (v3.7). */
export const PORTAL_AKTE_SCHREIBEN_HINT =
  'schreibt zum Chat-Anliegen; fehlendes in Klammern';
export const ARKO_AKTE_FUEHREN_EVENT = 'arkorbit:akte-fuehren';

/** Gleicher Trigger wie n8n `VARIANT_DEEPEN_RE` — Chip Variante 1/2/3. */
export const AKTE_VARIANT_DEEPEN_RE =
  /(?:welche\s+variante|variante\s*[123]\b|vertief(?:en|e|ung)\s+variante)/i;

export type AkteFuehrenChatActionId =
  | 'suchen'
  | 'zitieren'
  | 'verweisen'
  | 'zusammenfassen'
  | 'subsumieren'
  | 'verhandeln'
  | 'schreiben';

export type AkteFuehrenIntent =
  | 'document_question'
  | 'zusammenfassen'
  | 'erklaeren'
  | 'rechtsfrage'
  | 'schreiben_erstellen';

export const AKTE_FUEHREN_CHAT_ACTIONS: readonly AkteFuehrenChatActionId[] = [
  'suchen',
  'zitieren',
  'verweisen',
  'zusammenfassen',
  'subsumieren',
  'verhandeln',
  'schreiben',
] as const;

export const AKTE_FUEHREN_CHAT_LABELS: Record<AkteFuehrenChatActionId, string> = {
  suchen: 'Suchen',
  zitieren: 'Zitieren',
  verweisen: 'Verweisen',
  zusammenfassen: 'Zusammenfassen',
  subsumieren: 'Subsumieren',
  verhandeln: 'Verhandeln',
  schreiben: 'Schreiben',
};

/** Gleicher Trigger wie n8n `SACHVERHALT_ANALYSE_RE` — sonst kein LEXCORE-Subsumtions-Pfad. */
export const AKTE_SUBSUMTION_TRIGGER_RE =
  /\b(subsumtion|sachverhaltsanalys|analysier(?:e|en)?(?:\s+\w+){0,3}\s+(?:sachverhalt|fall))\b/i;

export function isAkteSubsumtionPrompt(text: string): boolean {
  return AKTE_SUBSUMTION_TRIGGER_RE.test(String(text || ''));
}

export interface AkteFuehrenPrompt {
  text: string;
  intent: AkteFuehrenIntent;
  autoSend: boolean;
}

export function formatAkteFuehrenDocList(titles: string[]): string {
  const clean = titles.map((t) => t.trim()).filter(Boolean);
  if (clean.length === 0) return 'den gewählten Akten-Dokumenten';
  if (clean.length === 1) return `„${clean[0]}"`;
  if (clean.length === 2) return `„${clean[0]}" und „${clean[1]}"`;
  const last = clean[clean.length - 1];
  return `${clean.slice(0, -1).map((t) => `„${t}"`).join(', ')} und „${last}"`;
}

export function akteFuehrenChatActionsReady(args: {
  akteBound: boolean;
  selectedWithExtractCount: number;
  extracting?: boolean;
}): boolean {
  return Boolean(args.akteBound) && args.selectedWithExtractCount > 0 && !args.extracting;
}

/** Chip-Tooltip: warum Suchen/Zitieren/… grau sind. */
export function akteFuehrenChatActionsHint(args: {
  ready: boolean;
  extracting?: boolean;
  failedCount?: number;
  action?: AkteFuehrenChatActionId;
}): string {
  if (args.ready) {
    if (args.action === 'subsumieren') {
      return 'Sachverhalt aus den angehakten Dokumenten gegen belegte Normen prüfen';
    }
    if (args.action === 'schreiben') {
      return 'Anwaltsschreiben zum Chat-Anliegen; Auszüge nur als Belege; fehlendes in Klammern';
    }
    if (args.action === 'zusammenfassen') {
      return 'Zusammenfassen: neutrale Inhaltsaufnahme der angehakten Auszüge — kein erfundenes Begehren';
    }
    return `${AKTE_FUEHREN_CHAT_LABELS[args.action ?? 'zusammenfassen']} mit den angehakten Akten-Dokumenten`;
  }
  if (args.extracting) return 'wird erkannt… — Aktionen danach klickbar';
  if ((args.failedCount ?? 0) > 0) {
    return 'Auszug fehlgeschlagen — Fehler am Haken, erneut anhaken oder Auszug kopieren';
  }
  return 'Dokumente anhaken — Auszug wird geladen, dann stehen die Aktionen bereit';
}

/** „Nicht relevant“: Doc aus der Auswahl, Rest bleibt. */
export function excludeIrrelevantAkteDoc(selectedIds: string[], documentId: string): string[] {
  const id = documentId.trim();
  if (!id) return selectedIds.filter(Boolean);
  return selectedIds.filter((x) => x.trim() && x.trim() !== id);
}

const AKTE_SUBSUMTION_TOPIC_RULES: ReadonlyArray<{ label: string; re: RegExp }> = [
  { label: 'Eigenbedarf', re: /\beigenbedarf/i },
  { label: 'ordentliche Kündigung', re: /ordentlich(?:e|en|er)?\s+k(?:ü|ue)ndig/i },
  { label: 'Kündigung', re: /\bk(?:ü|ue)ndig/i },
  { label: 'Befristung', re: /\bbefrist/i },
  { label: 'unbefristetes Mietverhältnis', re: /\bunbefrist/i },
  { label: 'Mietzins', re: /\b(?:haupt)?mietzins/i },
  { label: 'Mietvertrag', re: /\bmietvertrag|bestandverh(?:ältnis|altnis)/i },
];

const AKTE_LAW_NAME_RE = /\b(ABGB|MRG|WEG|KSchG|EO|ZPO|JN)\b/g;
const AKTE_DATE_RE = /\b(\d{1,2}\.\d{1,2}\.\d{4})\b/g;
const AKTE_PARTY_RE = /\b((?:ver)?mieter(?:in)?)\s*:\s*([^\n,;(]{2,80})/gi;
const AKTE_CITED_NORM_RE =
  /(?:§|§§)\s*\d+[a-z]?(?:\s*(?:Abs\.?|Absatz)\s*\d+)?\s+(?:ABGB|MRG|WEG|KSchG|EO|ZPO|JN)\b/gi;
const AKTE_FACT_LINE_RE =
  /eigenbedarf|k(?:ü|ue)ndig|befrist|mietzins|mietvertrag|bestandverh|\bMRG\b|\bABGB\b/i;
const AKTE_FACT_SKIP_RE = /PLACEHOLDER|Labor-Testdaten|keine Rechtsberatung|\bIBAN\b|\bBIC:/i;

export interface AkteSubsumtionFacts {
  topics: string[];
  laws: string[];
  dates: string[];
  parties: string[];
  citedNorms: string[];
  factLines: string[];
}

function uniqKeepOrder(items: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const key = raw.replace(/\s+/g, ' ').trim();
    if (!key) continue;
    const norm = key.toLowerCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    out.push(key);
  }
  return out;
}

/** Fließtext nach der Lazy-Extract-Kopfzeile, sonst der ganze String. */
export function stripAkteExtractBody(documentContext: string): string {
  const t = String(documentContext || '');
  const m = t.match(/Auszug \(UTF-8[^\n]*\):\s*\n?([\s\S]*)/i);
  return (m?.[1] || t).trim();
}

function collectMatches(blob: string, re: RegExp): string[] {
  const out: string[] = [];
  const flags = re.flags.includes('g') ? re.flags : `${re.flags}g`;
  const copy = new RegExp(re.source, flags);
  let m: RegExpExecArray | null;
  while ((m = copy.exec(blob)) !== null) {
    out.push((m[0] || '').trim());
    if (out.length >= 12) break;
  }
  return uniqKeepOrder(out);
}

function collectFactLines(raw: string): string[] {
  const body = stripAkteExtractBody(raw);
  const lines = body.split(/\r?\n/).map((l) => l.replace(/\s+/g, ' ').trim()).filter(Boolean);
  const out: string[] = [];
  for (const line of lines) {
    if (AKTE_FACT_SKIP_RE.test(line)) continue;
    if (line.length < 28) continue;
    if (!AKTE_FACT_LINE_RE.test(line)) continue;
    if (/^\s*(MIETVERTRAG|KÜNDIGUNG|KUENDIGUNG|KONTOBELEG)\b/i.test(line) && line.length < 48) continue;
    out.push(line.length > 180 ? `${line.slice(0, 177).trimEnd()}…` : line);
  }
  return out;
}

function scoreFactLine(line: string): number {
  let s = 0;
  if (/\beigenbedarf/i.test(line)) s += 50;
  if (/ordentlich(?:e|en|er)?\s+k(?:ü|ue)ndig/i.test(line)) s += 30;
  if (/\bk(?:ü|ue)ndig/i.test(line)) s += 15;
  if (/\bMRG\b/.test(line)) s += 10;
  if (/\bunbefrist|mietzins/i.test(line)) s += 4;
  return s;
}

function rankFactLines(lines: string[]): string[] {
  return uniqKeepOrder([...lines].sort((a, b) => scoreFactLine(b) - scoreFactLine(a))).slice(0, 4);
}

export function collectAkteSubsumtionFacts(extracts: string[]): AkteSubsumtionFacts {
  const bodies = extracts.map(stripAkteExtractBody).filter(Boolean);
  const blob = bodies.join('\n');
  const topics: string[] = [];
  for (const rule of AKTE_SUBSUMTION_TOPIC_RULES) {
    if (rule.re.test(blob)) topics.push(rule.label);
  }
  const parties: string[] = [];
  const partyCopy = new RegExp(AKTE_PARTY_RE.source, 'gi');
  let pm: RegExpExecArray | null;
  while ((pm = partyCopy.exec(blob)) !== null) {
    const role = (pm[1] || '').trim();
    const name = (pm[2] || '').replace(/\s+/g, ' ').trim();
    if (!role || !name) continue;
    parties.push(`${role} ${name}`);
    if (parties.length >= 4) break;
  }
  return {
    topics: uniqKeepOrder(topics),
    laws: collectMatches(blob, AKTE_LAW_NAME_RE),
    dates: collectMatches(blob.replace(/geb\.\s*\d{1,2}\.\d{1,2}\.\d{4}/gi, ''), AKTE_DATE_RE).slice(0, 5),
    parties: uniqKeepOrder(parties),
    citedNorms: collectMatches(blob, AKTE_CITED_NORM_RE).slice(0, 4),
    factLines: rankFactLines(extracts.flatMap(collectFactLines)),
  };
}

/** Suchfrage für LEXCORE — Stichworte aus Auszügen, keine erfundenen §§. */
export function buildAkteSubsumtionSearchText(
  docTitles: string[],
  extracts: string[] = [],
): string {
  const list = formatAkteFuehrenDocList(docTitles);
  const fromExtracts = collectAkteSubsumtionFacts(extracts);
  const fromTitles = collectAkteSubsumtionFacts([docTitles.join('\n')]);
  const facts: AkteSubsumtionFacts = {
    topics: uniqKeepOrder([...fromExtracts.topics, ...fromTitles.topics]),
    laws: fromExtracts.laws.length > 0 ? fromExtracts.laws : fromTitles.laws,
    dates: fromExtracts.dates,
    parties: fromExtracts.parties,
    citedNorms: fromExtracts.citedNorms,
    factLines: fromExtracts.factLines,
  };
  const parts: string[] = [
    `Analysiere folgenden Sachverhalt (Subsumtion) aus ${list}.`,
  ];
  const searchBits = uniqKeepOrder([...facts.topics, ...facts.laws]);
  if (searchBits.length > 0) {
    parts.push(`Suchfrage: ${searchBits.join(', ')}.`);
  } else {
    parts.push('Suchfrage: Mietrecht aus den angehakten Auszügen.');
  }
  if (facts.factLines.length > 0) {
    parts.push(`Sachverhalt: ${facts.factLines.join(' ')}`);
  }
  const meta: string[] = [];
  if (facts.dates.length > 0) meta.push(`Daten: ${facts.dates.join(', ')}`);
  if (facts.parties.length > 0) meta.push(`Parteien: ${facts.parties.join('; ')}`);
  if (facts.citedNorms.length > 0) {
    meta.push(`Im Auszug genannte Normen: ${facts.citedNorms.join(', ')}`);
  }
  if (meta.length > 0) parts.push(meta.join('. ') + '.');
  parts.push(
    'Suche belegte Normen zu diesen Stichworten. Keine Paragraphen erfinden, die nicht in den Auszügen stehen. '
      + 'Wenn nichts Passendes gefunden wird: in einem normalen Satz sagen, dass nichts gefunden wurde und man es anders versuchen soll. Keine allgemeine Orientierung und keine erfundenen §§.',
  );
  return parts.join(' ').replace(/\s+/g, ' ').trim();
}

const AKTE_CHIP_PROMPT_RE =
  /^(Formuliere ein Anwaltsschreiben|Formuliere ein Anschreiben|Analysiere folgenden Sachverhalt|Fasse |Zitiere die entscheidenden|Verweise präzise|Formuliere 3 kurze Verhandlungsfragen|Bitte vertiefen Sie Variante|Suche in )/i;

/** Vorheriger Chip-Text darf nicht als neues Chat-Anliegen gelten. */
export function isAkteChipGeneratedPrompt(text: string): boolean {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return false;
  if (AKTE_CHIP_PROMPT_RE.test(t)) return true;
  if (/Fallfakten aus den Auszügen/i.test(t)) return true;
  if (/Quellen und Belege, nicht die Aufgabenstellung/i.test(t)) return true;
  if (/Keine Aufgabenstellung und kein Begehren erfinden/i.test(t)) return true;
  return false;
}

export interface AkteSchreibenPromptOpts {
  chatConcern?: string;
  hasChat?: boolean;
}

/** Letztes getipptes Anliegen — Chip-Prompts überspringen. */
export function extractAkteSchreibenConcern(userTexts: string[]): AkteSchreibenPromptOpts {
  const cleaned = userTexts.map((t) => String(t || '').replace(/\s+/g, ' ').trim()).filter(Boolean);
  const human = cleaned.filter((t) => !isAkteChipGeneratedPrompt(t));
  const last = human[human.length - 1] || '';
  const chatConcern = last.length > 280 ? `${last.slice(0, 277).trimEnd()}…` : last;
  return { chatConcern, hasChat: human.length > 0 };
}

export function buildAkteSchreibenAuftrag(opts?: AkteSchreibenPromptOpts): string {
  const concern = String(opts?.chatConcern || '').replace(/\s+/g, ' ').trim();
  if (concern) return `Auftrag: ${concern}`;
  if (opts?.hasChat) return 'Auftrag: Schreiben zum im Chat genannten Anliegen.';
  return 'Auftrag: Anschreiben zum Sachverhalt der Akte.';
}

/**
 * Chip Zusammenfassen / Hintergrund analysieren: neutrale Inhaltsaufnahme.
 * Keyword-Scrape (kündigen, Wohnungsnummer) wird nicht zur Aufgabenstellung.
 */
export function buildAkteNeutralIntakePromptText(docTitles: string[]): string {
  const list = formatAkteFuehrenDocList(docTitles);
  return (
    `Fasse ${list} neutral für die Akte zusammen: was in den Auszügen steht (Kernaussagen, offene Punkte). `
    + 'Keine Aufgabenstellung und kein Begehren erfinden. '
    + 'Kein Kündigungs-Auftrag und keine Wohnungsnummer als Klageziel nur weil der Vertragstext solche Klauseln enthält. '
    + 'Die Auszüge sind Inhalt, nicht das Anliegen.'
  );
}

/**
 * Chip Schreiben: intent=schreiben_erstellen (nicht Verfassen/umformulieren).
 * Verb+Schriftstück im Text, damit n8n-Heuristik denselben Pfad trifft.
 * Kein „erstell/Entwurf + Mietvertrag“ — sonst Vertrag-Wizard statt Brief.
 * Auftrag = Chat-Anliegen; Auszüge nur Quellen/Belege, kein Keyword-Scrape.
 */
export function buildAkteSchreibenPromptText(
  docTitles: string[],
  extracts: string[] = [],
  opts?: AkteSchreibenPromptOpts,
): string {
  void extracts;
  const list = formatAkteFuehrenDocList(docTitles);
  const auftrag = buildAkteSchreibenAuftrag(opts);
  return [
    `Formuliere ein Anwaltsschreiben (Brief an die Gegenseite) zum genannten Anliegen. ${auftrag}`,
    `Die angehakten Auszüge aus ${list} sind Quellen und Belege, nicht die Aufgabenstellung.`,
    'Leite kein Begehren aus Vertragsklauseln ab (kein Kündigungs-Schreiben nur weil „kündigen“ oder eine Wohnungsnummer im Mietvertrag steht).',
    'Das ist ein Brief zum bestehenden Fall, kein neuer Vertrag und keine Interview-Schleife.',
    'Keine [Platzhalter] für Angaben, die im Chat oder in den Belegen stehen. Fehlende Parteien, Anschrift oder Daten als [Name der Gegenseite], [Anschrift] oder […]. Nichts erfinden (keine Aktenzeichen, Fristen, Paragraphen außerhalb der Auszüge). Zitate nur aus den Auszügen.',
  ].join(' ').replace(/\s+/g, ' ').trim();
}

export function buildAkteFuehrenPrompt(
  action: AkteFuehrenChatActionId,
  docTitles: string[],
  extracts?: string[],
  opts?: AkteSchreibenPromptOpts,
): AkteFuehrenPrompt {
  const list = formatAkteFuehrenDocList(docTitles);
  switch (action) {
    case 'suchen':
      return {
        text: `Suche in ${list} nach: `,
        intent: 'document_question',
        autoSend: false,
      };
    case 'zitieren':
      return {
        text: `Zitiere die entscheidenden Passagen aus ${list} wörtlich (kurze Auszüge, Dokumenttitel nennen). Keine neuen Paragraphen erfinden.`,
        intent: 'document_question',
        autoSend: true,
      };
    case 'verweisen':
      return {
        text: `Verweise präzise auf ${list}: welche Stelle trägt welches Argument? Nur die gewählten Dokumente.`,
        intent: 'document_question',
        autoSend: true,
      };
    case 'zusammenfassen':
      return {
        text: buildAkteNeutralIntakePromptText(docTitles),
        intent: 'zusammenfassen',
        autoSend: true,
      };
    case 'subsumieren':
      return {
        text: buildAkteSubsumtionSearchText(docTitles, extracts ?? []),
        intent: 'rechtsfrage',
        autoSend: true,
      };
    case 'verhandeln':
      return {
        text: `Formuliere 3 kurze Verhandlungsfragen zu ${list} (keine Rechtsberatung ersetzen).`,
        intent: 'erklaeren',
        autoSend: true,
      };
    case 'schreiben':
      return {
        text: buildAkteSchreibenPromptText(docTitles, extracts ?? [], opts),
        intent: 'schreiben_erstellen',
        autoSend: true,
      };
  }
}

export function dispatchAkteFuehrenChat(detail: AkteFuehrenPrompt): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ARKO_AKTE_FUEHREN_EVENT, { detail }));
}

export function isAkteVariantDeepenPrompt(text: string): boolean {
  return AKTE_VARIANT_DEEPEN_RE.test(String(text || ''));
}

/** `--- Titel ---` Blöcke aus dem LLM-Kontext (Inventar + Auszüge). */
export function parseAkteSelectedExtractsFromDocumentContext(
  documentContext: string | null | undefined,
): Array<{ title: string; extract: string }> {
  const t = String(documentContext || '');
  if (!t.trim()) return [];
  const out: Array<{ title: string; extract: string }> = [];
  const parts = t.split(/^--- (.+?) ---\s*$/m);
  if (parts.length >= 3) {
    for (let i = 1; i + 1 < parts.length; i += 2) {
      const title = String(parts[i] || '').trim();
      const extract = String(parts[i + 1] || '')
        .replace(/\nAkten-Stand:[\s\S]*$/u, '')
        .trim();
      if (title && extract) out.push({ title, extract });
      if (out.length >= 5) break;
    }
  }
  if (out.length > 0) return out;
  const focusSplit = t.split(/Auszug des Fokus-Dokuments:\s*/i);
  if (focusSplit.length > 1) {
    const body = String(focusSplit[1] || '')
      .replace(/\nAkten-Stand:[\s\S]*$/u, '')
      .trim();
    if (body) return [{ title: 'Fokus-Dokument', extract: body }];
  }
  return [];
}

/**
 * Follow-up Variante 1/2/3 nach Subsumtion: Allrounder, nicht LEXCORE.
 * Prompt muss VARIANT_DEEPEN_RE treffen und darf SACHVERHALT_ANALYSE_RE nicht treffen.
 */
export function buildAkteVariantDeepenPrompt(
  n: 1 | 2 | 3,
  docTitles: string[],
  extracts: string[] = [],
): AkteFuehrenPrompt {
  const variant = n === 1 || n === 2 || n === 3 ? n : 1;
  const list = formatAkteFuehrenDocList(docTitles);
  const facts = collectAkteSubsumtionFacts(extracts);
  const parts: string[] = [
    `Bitte vertiefen Sie Variante ${variant} aus Ihrer vorherigen Analyse anhand der angehakten Auszüge aus ${list}.`,
  ];
  const bits = uniqKeepOrder([...facts.topics, ...facts.laws]);
  if (bits.length > 0) {
    parts.push(`Stichworte aus den Auszügen: ${bits.join(', ')}.`);
  }
  if (facts.factLines.length > 0) {
    parts.push(`Stellen aus den Auszügen: ${facts.factLines.join(' ')}`);
  }
  parts.push(
    'Machen Sie die Variante an diesen Stellen fest. Keine neue Normsuche. Keine Paragraphen erfinden, die nicht in den Auszügen oder der vorherigen Analyse stehen.',
  );
  return {
    text: parts.join(' ').replace(/\s+/g, ' ').trim(),
    intent: 'erklaeren',
    autoSend: true,
  };
}
